import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Heart, ImageOff } from "lucide-react";
import { getEventById } from "../../services/eventService";
import { getGalleriesByEvent, setFundraisingBannerActive } from "../../services/galleryService";
import { uploadSingleFile } from "../../services/uploadService";
import Loader from "../common/Loader";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Badge from "../common/Badge";
import EmptyState from "../common/EmptyState";
import ConfirmModal from "../common/ConfirmModal";
import { color, radius } from "../../styles/theme";

/**
 * @Room Support Banner — ported from mobile's a-room-support-banner screen
 * (src/Screens/a-room-support-banner). Manages the donation/support banner
 * shown inside an @Room's gallery: an activate/deactivate toggle
 * (IsFundRaisingActive) and the banner image itself, both scoped to the
 * event's fundraising-flagged gallery (IsFundRaising === true).
 */
const bannerImageUrl = (gallery) => {
  const album = gallery?.Albums?.[0];
  const images = album?.Images || [];
  const thumb = images.find((img) => img?.IsAlbumThumbnail) || images[0];
  return thumb?.CompressedUrl || thumb?.Url || thumb?.WatermarkUrl || thumb?.ThumbnailUrl || null;
};

const SupportBannerModal = ({ eventId, onClose }) => {
  const { t } = useTranslation("rooms");
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      getEventById(eventId).catch(() => null),
      getGalleriesByEvent(eventId).catch(() => null),
    ]).then(([eventRes, galleriesRes]) => {
      setEvent(eventRes?.data?.Result || eventRes?.data || null);
      const galleries = galleriesRes?.data?.Result || [];
      setGallery(galleries.find((g) => g?.IsFundRaising) || null);
    }).finally(() => setLoading(false));
  };

  useEffect(load, [eventId]);

  const isActive = Boolean(gallery?.IsFundRaisingActive);

  const handleToggleStatus = async () => {
    if (!gallery?.Id) return;
    setUpdatingStatus(true);
    try {
      const res = await setFundraisingBannerActive(gallery.Id, eventId, !isActive);
      toast.success(res.data?.Message || (isActive ? t("supportBannerModal.toasts.deactivated") : t("supportBannerModal.toasts.activated")));
      setShowConfirm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("supportBannerModal.toasts.statusUpdateFailed"));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleImageSelect = async (file) => {
    if (!file) return;
    const albumId = gallery?.Albums?.[0]?.Id;
    if (!gallery?.Id || !albumId) {
      toast.warning(t("supportBannerModal.toasts.albumNotReady"));
      return;
    }
    setUploading(true);
    try {
      const res = await uploadSingleFile({
        file,
        chatRoomId: eventId,
        eventGalleryId: gallery.Id,
        galleryAlbumId: albumId,
        folderName: event?.Name || "Support_Banner",
        countryCode: event?.CountryCode,
        isCompressed: true,
      });
      if (res.data?.Status && res.data.Status !== "Success") throw new Error(res.data?.Message || "Failed");
      toast.success(t("supportBannerModal.toasts.imageUpdated"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.Message || err.message || t("supportBannerModal.toasts.imageUpdateFailed"));
    } finally {
      setUploading(false);
    }
  };

  const image = bannerImageUrl(gallery);

  return (
    <Modal
      title={t("supportBannerModal.title")}
      subtitle={gallery ? t("supportBannerModal.subtitle") : undefined}
      onClose={onClose}
      maxWidth={440}
    >
      {loading ? (
        <Loader />
      ) : !gallery ? (
        <EmptyState icon={ImageOff} text={t("supportBannerModal.emptyText")} />
      ) : (
        <>
          <div style={s.statusRow}>
            <Badge variant={isActive ? "success" : "danger"}>{isActive ? t("supportBannerModal.statusActive") : t("supportBannerModal.statusHidden")}</Badge>
            <span style={s.statusText}>{isActive ? t("supportBannerModal.visibleText") : t("supportBannerModal.hiddenText")}</span>
          </div>

          <div style={s.preview}>
            {image ? (
              <img src={image} alt={t("supportBannerModal.previewAlt")} style={s.previewImg} />
            ) : (
              <div style={s.previewEmpty}>{t("supportBannerModal.previewEmpty")}</div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) handleImageSelect(f); }}
          />

          <div style={s.actions}>
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              {uploading ? t("supportBannerModal.uploading") : t("supportBannerModal.updateImageButton")}
            </Button>
            <Button variant={isActive ? "danger" : "primary"} icon={Heart} onClick={() => setShowConfirm(true)} disabled={updatingStatus}>
              {isActive ? t("supportBannerModal.deactivateButton") : t("supportBannerModal.activateButton")}
            </Button>
          </div>
        </>
      )}

      {showConfirm && (
        <ConfirmModal
          title={isActive ? t("supportBannerModal.confirmDeactivateTitle") : t("supportBannerModal.confirmActivateTitle")}
          message={isActive
            ? t("supportBannerModal.confirmDeactivateMessage")
            : t("supportBannerModal.confirmActivateMessage")}
          confirmText={isActive ? t("supportBannerModal.confirmDeactivate") : t("supportBannerModal.confirmActivate")}
          danger={isActive}
          loading={updatingStatus}
          onConfirm={handleToggleStatus}
          onCancel={() => !updatingStatus && setShowConfirm(false)}
        />
      )}
    </Modal>
  );
};

const s = {
  statusRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 },
  statusText: { fontSize: 12.5, color: color.textSecondary },
  preview: {
    width: "100%", aspectRatio: "2.75", borderRadius: radius.sm, overflow: "hidden",
    background: color.gray50, border: `1px solid ${color.border}`, marginBottom: 16,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  previewImg: { width: "100%", height: "100%", objectFit: "cover" },
  previewEmpty: { fontSize: 12, color: color.textMuted },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" },
};

export default SupportBannerModal;
